'use client';

import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { Entity } from '@/lib/api';
import { Markdown } from './Markdown';
import { ImageLoader } from './ImageLoader';
import { ItemGridView } from './ItemGridView';
import { ActivityFeed, ActivityOperations } from '@/components/activities';
import { ExternalLink, BarChart3, Settings, Copy, Facebook, Instagram, Twitter, Linkedin, Youtube, Globe, Briefcase, MapPin, Menu, Info, Space } from 'lucide-react';
import { useSession } from '@/lib/auth-client';
import { extractTagValue } from '@/lib/utils';
import { EditEntityDialog } from '@/components/entities/EditEntityDialog';
import { NewEntityDialog } from '@/components/entities/NewEntityDialog';
import { FernmarkBadge } from '@/components/badges/FernmarkBadge';
import { useEntityPermissions } from '@/lib/hooks/useEntityPermissions';
import { EntityInitiatives } from '@/components/initiatives/EntityInitiatives';
import { FollowButton } from './FollowButton';
import { ENTITY_ROLE_OPTIONS, INITIATIVE_PUBLIC_STATUSES, type TradeHubSettings } from '@sizzlenz/shared';
import { ImageGallery } from '@/components/ui/image-gallery';
import { canAccessEntityTradeHub } from '@/lib/utils/trade';
import EnquiryForm from '@/components/enquiries/EnquiryForm';
import { ObjectConnections } from '@/components/ObjectConnections';
import { EntityProfileSnapshot } from '@/components/entities/EntityProfileSnapshot';
import { ObjectContentFeed } from '@/components/content/ObjectContentFeed';
import { LinkedHubs } from '@/components/LinkedHubs';
import * as _ from 'es-toolkit';
type SidebarPosition = 'right' | 'left' | 'under-description';
interface EntityDetailProps {
  entity: Entity;
  entityType: string;
  showProducts?: boolean;
  showConnections?: boolean;
  /** Controls where profile snapshot and enquiry form render */
  sidebarPosition?: SidebarPosition;
}
export function EntityDetail({
  entity,
  entityType,
  showConnections = false,
  sidebarPosition = 'right'
}: EntityDetailProps) {
  const [selectedImage, setSelectedImage] = useState(0);
  const [imageError, setImageError] = useState(!entity.image);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false);
  const showItems = _.isUndefined((entity.data?.main_page as any)?.showItems) ? entity.type == 'brand' : (entity.data?.main_page as any).showItems;
  const {
    data: sessionData
  } = useSession();
  // TODO: get infering in better auth working
  const userUpstockSupplierId = sessionData?.user?.data?.['upstock-supplier-id'];
  const entityUpstockSupplierId = extractTagValue(entity.tags, '@upstock-supplier-id=');
  const fernmarkId = extractTagValue(entity.systemTags, '@fernmark-id=');
  const isAdmin = sessionData?.user?.isAdmin === true;
  const userFeatureFlags = (sessionData?.user?.data as any)?.featureFlags;

  // Extract trade hub settings with proper typing
  const tradeHubSettings = (entity.data as any)?.trade_hub as TradeHubSettings | undefined;

  // Get entity permissions for permission-based UI
  const {
    hasPermission
  } = useEntityPermissions(entity.id);
  const canManageUsers = entityType === 'brand' && hasPermission('manage_members');
  const canEdit = hasPermission('edit');
  const canImportProducts = entityType === 'brand' && hasPermission('import_products');

  // Use the same access check as ObjectTradeHubView
  const canAccessTradeHub = tradeHubSettings?.enabled ? canAccessEntityTradeHub(tradeHubSettings, {
    isAdmin,
    featureFlags: userFeatureFlags,
    hasViewPermission: hasPermission('view_trade_hub')
  }) : false;

  // Mock multiple images (in real app, entity would have multiple images)
  const images = [entity.image];
  // Get gallery images from entity.data
  const galleryImages = useMemo(() => {
    return (entity.data as any)?.gallery?.images as string[] || [];
  }, [entity.data]);

  // Transform gallery images for react-image-gallery format
  const galleryItems = useMemo(() => {
    return galleryImages.map(url => ({
      original: url,
      thumbnail: url
    }));
  }, [galleryImages]);

  // Reset error state when image changes
  useEffect(() => {
    setImageError(!entity.image);
  }, [selectedImage, entity.image]);
  const getEntityIcon = () => {
    switch (entityType) {
      case 'brand':
        return <svg className="w-16 h-16 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>;
      case 'person':
        return <svg className="w-16 h-16 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>;
      case 'organisation':
        return <svg className="w-16 h-16 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>;
      default:
        return <svg className="w-16 h-16 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>;
    }
  };
  const upstockEntitySupplierUrl = useMemo(() => {
    if (userUpstockSupplierId && entityUpstockSupplierId)
      // @ts-ignore
      return `https://go.upstock.app/c/${userUpstockSupplierId}/buyer/suppliers/${entityUpstockSupplierId}`;
  }, [userUpstockSupplierId, entityUpstockSupplierId]);

  // Get website from entity.website or entity.data.website
  const websiteUrl = entity.website || (entity.data as any)?.website;

  // Get address from entity.data.address_1
  const addressDetail = (entity.data as any)?.address_1 as {
    formatted_address?: string;
    display_address?: string;
    url?: string;
  } | null | undefined;
  const displayAddress = addressDetail?.display_address || addressDetail?.formatted_address;

  // Get social media links from entity.data.links
  const enquiryTypes = useMemo(() => {
    const x = [];
    if (entityType == 'space') x.push({
      value: 'space',
      label: 'Space Enquiry',
      enabled: true
    });else x.push({
      value: 'general',
      label: 'General Enquiry',
      enabled: true
    });
    // TODO: detect entity type and add space enquiry if entity type = 'space'
    return x;
  }, [entity]);
  const socialLinks = useMemo(() => {
    const links = (entity.data as any)?.links;
    if (!Array.isArray(links)) return [];
    return links.map((link: {
      type?: string;
      platform?: string;
      username?: string;
      url?: string;
    }) => {
      const {
        platform,
        username,
        url: linkUrl,
        type = 'social'
      } = link;
      let url_0 = '';
      let icon = null;
      let displayName = platform;

      // If url is provided, use it directly; otherwise construct from username
      if (linkUrl) {
        url_0 = linkUrl;
      } else if (!username) {
        // Skip if neither url nor username is provided
        return null;
      }
      if (platform) {
        switch (platform.toLowerCase()) {
          case 'facebook':
            if (!linkUrl) url_0 = `https://facebook.com/${username}`;
            icon = <Facebook className="w-5 h-5" />;
            displayName = 'Facebook';
            break;
          case 'instagram':
            if (!linkUrl) url_0 = `https://instagram.com/${username}`;
            icon = <Instagram className="w-5 h-5" />;
            displayName = 'Instagram';
            break;
          case 'x':
          case 'twitter':
            if (!linkUrl) url_0 = `https://x.com/${username}`;
            icon = <Twitter className="w-5 h-5" />;
            displayName = 'X';
            break;
          case 'linkedin':
            if (!linkUrl) url_0 = `https://linkedin.com/company/${username}`;
            icon = <Linkedin className="w-5 h-5" />;
            displayName = 'LinkedIn';
            break;
          case 'youtube':
            if (!linkUrl) url_0 = `https://youtube.com/${username}`;
            icon = <Youtube className="w-5 h-5" />;
            displayName = 'YouTube';
            break;
          case 'tiktok':
            if (!linkUrl) url_0 = `https://tiktok.com/@${username}`;
            icon = <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                </svg>;
            displayName = 'TikTok';
            break;
          default:
            return null;
        }
      } else {
        switch (type) {
          case 'booking':
            url_0 = linkUrl;
            icon = <Globe className="w-5 h-5" />;
            displayName = 'Booking Link';
            break;
          case 'menu':
            url_0 = linkUrl;
            icon = <Menu className="w-5 h-5" />;
            displayName = 'Menu link';
            break;
          case 'functions-page':
            url_0 = linkUrl;
            icon = <Space className="w-5 h-5" />;
            displayName = 'Functions Page';
            break;
          case 'functions-pack':
            url_0 = linkUrl;
            icon = <Info className="w-5 h-5" />;
            displayName = 'Functions Packs';
            break;
        }
      }
      return {
        platform: displayName,
        url: url_0,
        icon,
        username
      };
    }).filter(Boolean);
  }, [entity.data]);
  const tagsToShow = entity.tags.filter(tag => !tag.startsWith('@'));
  const roleLabels = useMemo(() => {
    const roles = (entity.data as any)?.roles;
    if (!Array.isArray(roles) || roles.length === 0) return [];
    return roles.map((roleId: string) => ENTITY_ROLE_OPTIONS.find(r => r.id === roleId)?.label).filter(Boolean) as string[];
  }, [entity.data]);

  // Extract showInitiatives with proper memoization to ensure re-renders
  const showInitiatives = !!(entity.data as any)?.main_page?.showInitiatives;
  const showEnquiryForm = !!(entity.data as any)?.main_page?.showEnquiryForm;
  const showContentFeed = !!(entity.data as any)?.main_page?.showContentFeed;
  const contentFeedTitle = (entity.data as any)?.main_page?.contentFeedTitle || 'Content';

  // Engagement settings (reactions default to true for backwards compatibility, comments default to off)
  const allowReactions = (entity.data as any)?.allowReactions !== false;
  const allowComments = (entity.data as any)?.allowComments === true;
  return <div className="">
      {/* Header: Entity Info (single column layout) */}
      <div className="relative">
        {/* Action Buttons - Only show if user has permission */}
        {(canEdit || isAdmin) && <div className="absolute top-0 right-0 flex items-center gap-2 z-10">
            {isAdmin && <div className="text-gray-400 cursor-pointer hover:text-gray-600" title="Clone Entity">
                <Copy onClick={() => setIsCloneDialogOpen(true)} className="w-5 h-5" />
              </div>}
            {canEdit && <div className="text-gray-400 cursor-pointer hover:text-gray-600" title="Edit Entity">
                <Settings onClick={() => setIsEditDialogOpen(true)} className="w-5 h-5" />
              </div>}
          </div>}

        {/* Image + Title Row */}
        <div className="flex items-start gap-6">
          {/* Inline Cover Image */}
          <div className="relative bg-white rounded-2xl overflow-hidden border border-gray-200 w-[120px] h-[120px] md:w-[180px] md:h-[180px] flex-shrink-0">
            {imageError ? <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                <div className="text-center p-4">{getEntityIcon()}</div>
              </div> : <ImageLoader src={images[selectedImage] || ''} alt={entity.name_1 || 'Entity image'} width={180} height={180} className="w-full h-full object-contain" onError={() => setImageError(true)} fallback={<div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                    <div className="text-center p-4">{getEntityIcon()}</div>
                  </div>} />}
          </div>

          {/* Title and key info beside the image */}
          <div className="flex-1 min-w-0">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              {entity.name_1 || 'Unnamed Entity'}
            </h1>
            {entity.name_2 && <h2 className="text-xl text-gray-600 mb-4">{entity.name_2}</h2>}
            {/* Roles Display */}
            {roleLabels.length > 0 && <div className="pt-1">
                <div className="flex flex-wrap gap-2">
                  {roleLabels.map(label => <span key={label} className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg border border-blue-200">
                      {label}
                    </span>)}
                </div>
              </div>}

            {(isAdmin || canManageUsers || canEdit || canImportProducts || canAccessTradeHub) && <div className="mt-2 mb-2 flex items-center gap-3">
                {(isAdmin || canManageUsers || canEdit || canImportProducts) && <Link href={`/entities/${entity.id}/admin`} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm hover:shadow-md font-medium cursor-pointer">
                    <BarChart3 className="h-5 w-5" />
                    Dashboard
                  </Link>}
                {canAccessTradeHub && <Link href={`/entities/${entity.id}/trade-hub`} className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm hover:shadow-md font-medium cursor-pointer">
                    <Briefcase className="h-5 w-5" />
                    {tradeHubSettings?.ui?.hubButtonLabel || 'Trade Hub'}
                  </Link>}
              </div>}

            {/* Linked Hubs */}
            <LinkedHubs systemTags={entity.systemTags} cachedHubs={(entity.data as any)?.hubs} />

            {/* Tags */}
            {tagsToShow && tagsToShow.length > 0 && <div className="flex flex-wrap gap-2 mt-1 mb-2">
                {tagsToShow.map(tag_0 => <span key={tag_0} className="bg-gray-100 text-gray-700 text-sm px-3 py-1 rounded-full">
                    {tag_0}
                  </span>)}
              </div>}
          </div>
        </div>

        {/* Thumbnail Gallery (if multiple images) */}
        {images.length > 1 && <div className="grid grid-cols-4 gap-2 mt-4 max-w-[300px]">
            {images.map((image, index) => <button key={index} onClick={() => setSelectedImage(index)} className={clsx('relative aspect-square rounded-lg overflow-hidden border-2 transition-all', selectedImage === index ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-200 hover:border-gray-300')}>
                <ImageLoader src={image || ''} alt={`${entity.name_1} ${index + 1}`} width={200} height={200} className="w-full h-full" />
              </button>)}
          </div>}

        {/* Follow Button and Activity Operations Row */}
        <div className="flex items-start justify-between mt-3 mb-4 gap-4">
          {sessionData?.user?.approved && entity.following !== undefined ? <FollowButton objectId={entity.id} objectType="entity" following={entity.following} showLabel /> : <div />}
          <div className="flex items-center">
            {allowReactions && <ActivityOperations dialogEnabled showVotes={false} objectId={String(entity.id)} objectType="entity" title={entity.name_1 || 'Unnamed Entity'} />}
          </div>
        </div>

        {/* Contact Information */}
        {(entity.primary_email || entity.primary_phone || displayAddress || websiteUrl || socialLinks.length > 0 || entity.systemTags?.includes('@channel=upstock')) && <div className="space-y-1">
            {entity.primary_email && <div className="flex items-center gap-3 text-gray-700">
                <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href={`mailto:${entity.primary_email}`} className="text-primary-600 hover:text-primary-700 hover:underline">
                  {entity.primary_email}
                </a>
              </div>}
            {entity.primary_phone && <div className="flex items-center gap-3 text-gray-700">
                <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <a href={`tel:${entity.primary_phone}`} className="text-primary-600 hover:text-primary-700 hover:underline">
                  {entity.primary_phone}
                </a>
              </div>}

            {displayAddress && <div className="flex items-center gap-3 text-gray-700">
                <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0" />
                {addressDetail.url ? <a href={addressDetail.url} target="_blank" rel="noopener noreferrer" className="hover:underline cursor-pointer">
                    {displayAddress}
                  </a> : <span>{displayAddress}</span>}
              </div>}

            {/* Website and Social Media Links */}
            {(websiteUrl || socialLinks.length > 0) && <div className="pt-2">
                <div className="flex flex-wrap gap-3">
                  {websiteUrl && <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors cursor-pointer" title={websiteUrl}>
                      <Globe className="w-5 h-5" />
                    </a>}
                  {socialLinks.map((link_0: any) => <a key={link_0.platform} href={link_0.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors cursor-pointer" title={`${link_0.platform}`}>
                      {link_0.icon}
                    </a>)}
                </div>
              </div>}

            {upstockEntitySupplierUrl && <div className="flex items-center gap-3 pt-2">
                <a href={upstockEntitySupplierUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-sm hover:shadow-md font-medium">
                  Wholesale order on Upstock
                  <ExternalLink />
                </a>
              </div>}
            {entityUpstockSupplierId && !upstockEntitySupplierUrl && <div>
                Wholesale ordering available on{' '}
                <a href="https://upstock.app" target="_blank">
                  Upstock
                </a>
              </div>}
          </div>}

        {/* Fernmark Badge */}
        {/* {entity.systemTags?.includes('@fernmark') && (
          <FernmarkBadge fernmarkId={fernmarkId} className="mt-3 mb-4" />
         )} */}

        {/* Description + Sidebar Layout */}
        {sidebarPosition === 'under-description' ? <>
            {/* Description as Markdown */}
            {entity.description && <Markdown className="mt-6 text-gray-700 leading-relaxed">
                {entity.description}
              </Markdown>}
            {/* Profile Snapshot & Enquiry - Under Description */}
            <div className="space-y-6 mt-8">
              <EntityProfileSnapshot data={entity.data as Record<string, unknown> || {}} />
              {showEnquiryForm && <EnquiryForm modalSignup sessionRequiredToSubmit showSubject showType={true} context="entity-enquiry" apiRoute="entities/enquiries" resourceType="entity" resourceId={entity.id} enquiryTypes={enquiryTypes} />}
            </div>
          </> : <>
            {/* Description + Sidebar in 2-column grid (left or right) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
              {/* Description Column */}
              <div className={clsx('lg:col-span-2', sidebarPosition === 'left' ? 'order-2' : 'order-1')}>
                {entity.description && <Markdown className="text-gray-700 leading-relaxed">
                    {entity.description}
                  </Markdown>}
              </div>

              {/* Sidebar Column - profile snapshot visible always, enquiry form only on lg+ */}
              <div className={clsx('space-y-6', sidebarPosition === 'left' ? 'order-1' : 'order-2')}>
                <EntityProfileSnapshot data={entity.data as Record<string, unknown> || {}} />
                {showEnquiryForm && <div className="hidden lg:block">
                    <EnquiryForm modalSignup sessionRequiredToSubmit showSubject showType={true} context="entity-enquiry" apiRoute="entities/enquiries" resourceType="entity" resourceId={entity.id} enquiryTypes={enquiryTypes} />
                  </div>}
              </div>
            </div>
          </>}
      </div>

      {/* Main Content (full-width below description area) */}
      <div className="space-y-10 mt-8">
        {/* Initiatives Section */}
        {showInitiatives && <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {(entity.data as any)?.initiatives?.sectionTitle || 'Offers and Campaigns'}
              </h2>
              {(entity.data as any)?.initiatives?.description && <p className="text-gray-600">{(entity.data as any).initiatives.description}</p>}
            </div>
            <EntityInitiatives status={[...INITIATIVE_PUBLIC_STATUSES]} entityId={entity.id} entityType="entity" limit={12} excludeTemplates showTradeOnlyBadge />
          </div>}

        {/* Content Feed Section */}
        {showContentFeed && <ObjectContentFeed showViewModeToggle={true} showListImage={false} searchEnabled={true} syncToUrl={false} ownerType="entity" ownerId={String(entity.id)} linkedRecords={[{
        objectId: entity.id,
        objectType: 'entity'
      }]} title={contentFeedTitle} backLabel={entity.name_1 || 'Entity'} backHref={`/entities/${entity.id}`} />}

        {/* Items Section */}
        {showItems && <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {(entity.data.items as any)?.sectionTitle || 'Products'}
              </h2>
            </div>
            <ItemGridView brandId={entity.id} variant="embedded" searchEnabled={false} canFilter={false} limit={20} emptyState={{
          title: 'No products found',
          description: "This brand doesn't have any products yet."
        }} />
          </div>}

        {/* Connections (Relationships) */}
        {showConnections && <ObjectConnections fromType="entity" fromId={entity.id} title="Relationships" showRelationships />}

        {/* Gallery Section */}
        {galleryItems.length > 0 && <div>
            <ImageGallery items={galleryItems} className="rounded-2xl overflow-hidden" />
          </div>}
      </div>

      {/* Enquiry Form on mobile - shown below main content sections (profile snapshot stays above) */}
      {sidebarPosition !== 'under-description' && showEnquiryForm && <div className="mt-8 lg:hidden">
          <EnquiryForm modalSignup sessionRequiredToSubmit showSubject showType={true} context="entity-enquiry" apiRoute="entities/enquiries" resourceType="entity" resourceId={entity.id} enquiryTypes={enquiryTypes} />
        </div>}

      {/* Activity Feed Section */}
      {allowComments && <div className="border-t border-gray-200 pt-8 mt-12 space-y-8">
          <ActivityFeed canComment objectId={String(entity.id)} objectType="entity" />
        </div>}

      {/* Edit Entity Dialog */}
      {canEdit && <EditEntityDialog isOpen={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)} entity={entity} />}

      {/* Clone Entity Dialog */}
      {isAdmin && <NewEntityDialog isOpen={isCloneDialogOpen} onClose={() => setIsCloneDialogOpen(false)} cloneFrom={entity} defaultType={entity.type} allowedTypes={[entity.type]} title={`Clone ${entity.name_1}`} />}
    </div>;
}